import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, MessageCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isNearExpiration, formatShortDate } from './utils';
import type { MemberData } from './types';

interface PaymentRemindersSectionProps {
  members: MemberData[];
  onContactMember: (member: MemberData, message: string) => void;
}

const planPrices: Record<string, Record<string, number>> = {
  bronce: { mensual: 5000, anual: 45000 },
  plata: { mensual: 12000, anual: 100000 },
  oro: { mensual: 20000, anual: 180000 },
};

export const PaymentRemindersSection: React.FC<PaymentRemindersSectionProps> = ({
  members,
  onContactMember,
}) => {
  // Filter active members with upcoming expirations
  const membersWithExpiration = members
    .filter(m => m.membership && m.membership.is_active)
    .map(member => {
      const expirationStatus = isNearExpiration(member.membership);
      if (!expirationStatus.isExpiring) return null;
      
      return {
        member,
        daysLeft: expirationStatus.daysLeft,
        urgency: expirationStatus.urgency,
      };
    })
    .filter(Boolean) as { member: MemberData; daysLeft: number; urgency: 'warning' | 'critical' }[];

  // Sort by urgency (critical first) then by days left
  membersWithExpiration.sort((a, b) => {
    if (a.urgency === 'critical' && b.urgency !== 'critical') return -1;
    if (a.urgency !== 'critical' && b.urgency === 'critical') return 1;
    return a.daysLeft - b.daysLeft;
  });

  const generateRenewalMessage = (member: MemberData, daysLeft: number): string => {
    const firstName = member.profile.nombre.split(' ')[0];
    const plan = member.membership?.plan || 'bronce';
    const billingPeriod = member.membership?.billing_period || 'mensual';
    const endDate = member.membership?.end_date 
      ? formatShortDate(member.membership.end_date) 
      : '';
    const price = planPrices[plan]?.[billingPeriod] || 0;
    const formattedPrice = price.toLocaleString('es-CR');

    return `¡Hola ${firstName}! 👋

Te recordamos que tu membresía VillaTech ${plan.charAt(0).toUpperCase() + plan.slice(1)} vence en ${daysLeft} día${daysLeft !== 1 ? 's' : ''} (${endDate}).

Para renovar y seguir disfrutando de tus beneficios:
💳 Plan: ${plan.charAt(0).toUpperCase() + plan.slice(1)}
📅 Período: ${billingPeriod.charAt(0).toUpperCase() + billingPeriod.slice(1)}
💰 Precio: ₡${formattedPrice}

¿Te gustaría coordinar tu pago? Estamos para ayudarte 🙌`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/20"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
          <CreditCard className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-lg">Recordatorios de Pago</h3>
          <p className="text-amber-400/80 text-sm">
            {membersWithExpiration.length} membresía{membersWithExpiration.length !== 1 ? 's' : ''} próxima{membersWithExpiration.length !== 1 ? 's' : ''} a vencer
          </p>
        </div>
      </div>

      {membersWithExpiration.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-green-500/10 mx-auto mb-3 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <p className="text-green-400 font-medium">¡Todo en orden!</p>
          <p className="text-white/50 text-sm">No hay membresías próximas a vencer</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {membersWithExpiration.map(({ member, daysLeft, urgency }) => {
            const plan = member.membership?.plan || 'bronce';
            const billingPeriod = member.membership?.billing_period || 'mensual';
            const price = planPrices[plan]?.[billingPeriod] || 0;

            return (
              <div
                key={member.profile.user_id}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  urgency === 'critical'
                    ? 'bg-red-500/10 border border-red-500/30'
                    : 'bg-amber-500/10 border border-amber-500/20'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    urgency === 'critical' ? 'bg-red-500/20' : 'bg-amber-500/20'
                  }`}>
                    <AlertTriangle className={`w-4 h-4 ${
                      urgency === 'critical' ? 'text-red-400' : 'text-amber-400'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white text-sm font-medium truncate">
                        {member.profile.nombre}
                      </p>
                      <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                        plan === 'oro' 
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : plan === 'plata'
                          ? 'bg-slate-400/20 text-slate-300'
                          : 'bg-amber-600/20 text-amber-400'
                      }`}>
                        {plan.charAt(0).toUpperCase() + plan.slice(1)}
                      </span>
                    </div>
                    <p className={`text-xs ${
                      urgency === 'critical' ? 'text-red-400' : 'text-amber-400/70'
                    }`}>
                      {daysLeft === 0 ? 'Vence hoy' : `${daysLeft} día${daysLeft !== 1 ? 's' : ''}`} • ₡{price.toLocaleString('es-CR')} {billingPeriod}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onContactMember(member, generateRenewalMessage(member, daysLeft))}
                  className={`shrink-0 ml-2 ${
                    urgency === 'critical'
                      ? 'border-red-500/30 text-red-400 hover:bg-red-500/20'
                      : 'border-green-500/30 text-green-400 hover:bg-green-500/20'
                  }`}
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  WhatsApp
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default PaymentRemindersSection;